import React, { useRef, useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import "../assets/splash.css"; // Import the CSS file
import { Link, useNavigate } from "react-router-dom";
import {
  IconButton,
  Tooltip,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import StarIcon from "@mui/icons-material/Star";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import VerifiedIcon from "@mui/icons-material/Verified";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

/**
 * SplashScreen component - Entry point for the application
 * This is a standalone page that doesn't use PageContainer or sidebar
 */
const SplashScreen = () => {
  const navigate = useNavigate();
  const { userInfo, authLoading } = useContext(AuthContext);
  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && userInfo) {
      navigate("/");
    }
  }, [userInfo, authLoading, navigate]);
  // Refs for the carousels
  const boys = [
    "https://s3media.247sports.com/Uploads/Assets/982/434/12434982.jpeg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/625/481/12481625.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/979/434/12434979.jpeg",
    "https://s3media.247sports.com/Uploads/Assets/341/824/11824341.jpeg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/895/860/11860895.jpeg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/841/794/11794841.jpeg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/654/823/11823654.jpeg",
    "https://hoopseen.com/sites/default/files/styles/profile_square/public/2023-06/Hannah%20head.jpg",
    "https://s3media.247sports.com/Uploads/Assets/60/435/12435060.jpeg",
    "https://s3media.247sports.com/Uploads/Assets/97/708/12708097.jpg",
  ];

  const boysStats = [
    { ppg: 24, rpg: 8 },
    { ppg: 19, rpg: 6 },
    { ppg: 22, rpg: 7 },
    { ppg: 18, rpg: 9 },
    { ppg: 21, rpg: 5 },
    { ppg: 17, rpg: 10 },
    { ppg: 26, rpg: 6 },
    { ppg: 15, rpg: 7 },
    { ppg: 20, rpg: 8 },
    { ppg: 16, rpg: 11 },
  ];

  const girls = [
    "https://s3media.247sports.com/Uploads/Assets/218/449/12449218.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/444/456/12456444.png?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/321/467/12467321.jpeg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/558/449/12449558.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/329/456/12456329.jpeg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/316/582/12582316.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/311/596/12596311.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/366/582/12582366.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/348/582/12582348.jpg?width=70&fit=crop",
    "https://s3media.247sports.com/Uploads/Assets/333/582/12582333.png?width=70&fit=crop",
  ];

  const girlsStats = [
    { ppg: 22, rpg: 7 },
    { ppg: 18, rpg: 5 },
    { ppg: 20, rpg: 6 },
    { ppg: 17, rpg: 8 },
    { ppg: 25, rpg: 4 },
    { ppg: 19, rpg: 9 },
    { ppg: 21, rpg: 6 },
    { ppg: 16, rpg: 7 },
    { ppg: 23, rpg: 5 },
    { ppg: 24, rpg: 8 },
  ];

  const boysCarouselRef = useRef(null);
  const girlsCarouselRef = useRef(null);
  const [isTouching, setIsTouching] = useState(false);
  const [activeBoyIndex, setActiveBoyIndex] = useState(0);
  const [activeGirlIndex, setActiveGirlIndex] = useState(0);

  // Function to scroll the carousel left or right
  const scrollCarousel = (carouselRef, direction, isBoysCarousel = true) => {
    if (!carouselRef.current) return;

    const scrollAmount = 200; // Adjust as needed
    const currentScroll = carouselRef.current.scrollLeft;
    const scrollWidth = carouselRef.current.scrollWidth;
    const clientWidth = carouselRef.current.clientWidth;
    const itemWidth = 180 + 15; // Width + gap

    // Calculate the current visible "page" (approximate)
    const currentIndex = Math.round(currentScroll / itemWidth);
    let newIndex;

    // Determine new index based on direction
    if (direction === "left") {
      newIndex = Math.max(0, currentIndex - 1);
    } else {
      const maxIndex = Math.floor(boys.length - clientWidth / itemWidth) + 1;
      newIndex = Math.min(maxIndex, currentIndex + 1);
    }

    // Update active index for animations/indicators
    if (isBoysCarousel) {
      setActiveBoyIndex(newIndex);
    } else {
      setActiveGirlIndex(newIndex);
    }

    // Scroll to the position based on item width
    carouselRef.current.scrollTo({
      left: newIndex * itemWidth,
      behavior: "smooth",
    });
  };

  // Touch handlers for mobile scrolling
  const handleTouchStart = (carouselRef) => {
    if (!carouselRef.current) return;
    setIsTouching(true);
    carouselRef.current.classList.add("scrolling");
  };

  const handleTouchEnd = (carouselRef) => {
    if (!carouselRef.current) return;
    setIsTouching(false);
    carouselRef.current.classList.remove("scrolling");
  };

  // Placeholder data based on XSM info
  const trendingItems = [
    { number: 1, title: "Advanced Stats" },
    { number: 2, title: "Performance Analytics" },
    { number: 3, title: "Player Insights" },
    { number: 4, title: "Team Metrics" },
    { number: 5, title: "Scouting Reports" },
    { number: 6, title: "Recruiting Profiles" },
    { number: 7, title: "Game Analysis" },
    { number: 8, title: "Game Analysis" },
    { number: 9, title: "Game Analysis" },
    { number: 10, title: "Game Analysis" },
  ];

  const features = [
    {
      title: "Verified Box Score Data",
      description:
        "We use VERIFIED box score data and advanced metrics to identify players on an integrated platform.",
      icon: <VerifiedIcon fontSize="large" />,
      color: "#4CAF50", // green
      stats: ["99% accuracy rate", "10,000+ verified games"],
      bgImage:
        "url('https://cdn.prod.website-files.com/66842e04d18971242a294872/67035d5902d63e122592eb37_Managing_Shot_Clock_in_Basketball.png')",
    },
    {
      title: "Real-Time Analytics",
      description:
        "Access comprehensive data analytics on high school athletes across various levels.",
      icon: <BarChartIcon fontSize="large" />,
      color: "#5D87FF", // blue
      stats: ["Live game tracking", "AI-powered insights"],
      bgImage:
        "url('https://images.unsplash.com/photo-1551288049-bebda4e38f71')",
    },

    {
      title: "Transfer Portal Integration",
      description: "Access the latest transfer portal updates for athletes.",
      icon: <SchoolIcon fontSize="large" />,
      color: "#9C27B0", // purple
      stats: ["Real-time transfer alerts", "Xfactor scores"],
      bgImage:
        "url('https://images.unsplash.com/photo-1577471488278-16eec37ffcc2?ixlib=rb-4.0.3')",
    },
    {
      title: "Youth-to-Pro Data Pipeline",
      description:
        "We provide a data pipeline for youth-to-pro athletes, from high school to the pros.",
      icon: <TimelineIcon fontSize="large" />,
      color: "#FF5722", // orange
      stats: ["15+ key metrics tracked", "Custom performance dashboards"],
      bgImage:
        "url('https://images.unsplash.com/photo-1688485423255-e4f805241222')",
    },
  ];

  const faqItems = [
    {
      question: "What is XSM?",
      answer:
        "A sports tech platform that tracks and analyzes basketball performance from high school to the pros.",
    },
    {
      question: "How does XSM help athletes?",
      answer:
        "We remove bias in recruiting by using data to highlight true player efficiency.",
    },
    {
      question: "What sports does XSM cover?",
      answer: "Basketball only-for now. More sports coming soon.",
    },
    {
      question: "How accurate is the data?",
      answer:
        "99% accuracy, verified by certified statisticians and trained spotters.",
    },
    {
      question: "How can I get started?",
      answer:
        "Players in our system can subscribe. Teams and schools subscribe to access full data insights.",
    },
    {
      question: "What metrics do you track?",
      answer:
        "We measure individual efficiency and compare athletes across a standardized data model.",
    },
  ];

  // Track which feature is being hovered
  const [hoveredFeature, setHoveredFeature] = useState(null);

  // Track which FAQ item is expanded
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Toggle FAQ expansion
  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // Track if features are in view for animations
  const featuresRef = useRef(null);
  const [featuresInView, setFeaturesInView] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Check if screen is mobile width
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if features section is in view
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setFeaturesInView(true);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.2, // Trigger when 20% of the element is visible
    });

    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }

    return () => {
      if (featuresRef.current) {
        observer.unobserve(featuresRef.current);
      }
    };
  }, []);

  return (
    <div className="splash-screen">
      {/* Header & Hero Section (Splash1) */}
      <header
        className="splash-screen-header-hero"
        style={{
          backgroundImage: `url(${"/xsm_home.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="splash-screen-header">
          <div className="splash-screen-logo">
            <img src="/xsmlgsm.PNG" alt="XSM Logo" />
          </div>
          <div className="splash-screen-nav">
            <Link to="/login" className="text-decoration-none">
              <button className="splash-screen-button splash-screen-button-primary">
                Sign In
              </button>
            </Link>
          </div>
        </div>
        <div className="splash-screen-hero">
          <h1>
            <span className="splash-screen-title-first">Unleash the</span>{" "}
            <span className="splash-screen-x-factor">
              <img src="/xsmX.png" alt="X" className="splash-screen-x-logo" />
              <span className="splash-screen-factor-text">Factor</span>
            </span>
          </h1>
          <p>The World's First Youth-to-Pro Pipeline</p>
          <Link to="/signup" className="text-decoration-none">
            <button className="splash-screen-button splash-screen-button-primary splash-screen-button-large">
              <span className="splash-screen-button-content">
                Get Started{" "}
                <ArrowForwardRoundedIcon className="splash-screen-button-icon" />
              </span>
            </button>
          </Link>
        </div>
      </header>

      {/* Trending Boys Section */}
      <section className="splash-screen-trending">
        <Box className="splash-screen-trending-header">
          <Typography
            variant="h5"
            component="h2"
            className="splash-screen-trending-title"
          >
            <SportsBasketballIcon className="splash-screen-trending-icon" />{" "}
            Trending Boys
          </Typography>
          <Typography
            variant="subtitle1"
            className="splash-screen-trending-subtitle"
          >
            Top prospects making waves this week
          </Typography>
        </Box>

        <div className="splash-screen-carousel-container">
          {!isMobile && (
            <Tooltip title="Previous" placement="left">
              <IconButton
                className="splash-screen-carousel-arrow left"
                onClick={() => scrollCarousel(boysCarouselRef, "left", true)}
                size="large"
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            </Tooltip>
          )}

          <div
            className="splash-screen-trending-carousel"
            ref={boysCarouselRef}
            onTouchStart={() => handleTouchStart(boysCarouselRef)}
            onTouchEnd={() => handleTouchEnd(boysCarouselRef)}
          >
            {boys.map((imageUrl, index) => (
              <div
                key={index}
                className={`splash-screen-trending-item ${
                  !isMobile && index === activeBoyIndex
                    ? "splash-screen-trending-item-active"
                    : ""
                }`}
              >
                <div
                  className="splash-screen-trending-placeholder splash-screen-athlete-image"
                  style={{
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="splash-screen-trending-badge">
                    <span className="splash-screen-trending-number">
                      {index + 1}
                    </span>
                    {index < 3 && (
                      <WhatshotIcon className="splash-screen-trending-fire" />
                    )}
                  </div>
                  <div className="splash-screen-trending-player-info">
                    <StarIcon className="splash-screen-trending-star" />
                    <div className="splash-screen-trending-player-stats">
                      <span className="splash-screen-trending-stat">
                        PPG: {boysStats[index].ppg}
                      </span>
                      <span className="splash-screen-trending-stat">
                        RPG: {boysStats[index].rpg}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!isMobile && (
            <Tooltip title="Next" placement="right">
              <IconButton
                className="splash-screen-carousel-arrow right"
                onClick={() => scrollCarousel(boysCarouselRef, "right", true)}
                size="large"
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div className="splash-screen-carousel-indicators">
          {boys.slice(0, Math.min(5, boys.length)).map((_, index) => (
            <div
              key={index}
              className={`splash-screen-carousel-indicator ${
                index === activeBoyIndex ? "active" : ""
              }`}
              onClick={() => {
                if (boysCarouselRef.current) {
                  const itemWidth = 180 + 15; // Width + gap
                  boysCarouselRef.current.scrollTo({
                    left: index * itemWidth,
                    behavior: "smooth",
                  });
                  setActiveBoyIndex(index);
                }
              }}
            />
          ))}
        </div>
      </section>

      {/* Trending Girls Section */}
      <section className="splash-screen-trending">
        <Box className="splash-screen-trending-header">
          <Typography
            variant="h5"
            component="h2"
            className="splash-screen-trending-title"
          >
            <SportsBasketballIcon className="splash-screen-trending-icon" />{" "}
            Trending Girls
          </Typography>
          <Typography
            variant="subtitle1"
            className="splash-screen-trending-subtitle"
          >
            Top prospects making waves this week
          </Typography>
        </Box>

        <div className="splash-screen-carousel-container">
          {!isMobile && (
            <Tooltip title="Previous" placement="left">
              <IconButton
                className="splash-screen-carousel-arrow left"
                onClick={() => scrollCarousel(girlsCarouselRef, "left", false)}
                size="large"
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            </Tooltip>
          )}

          <div
            className="splash-screen-trending-carousel"
            ref={girlsCarouselRef}
            onTouchStart={() => handleTouchStart(girlsCarouselRef)}
            onTouchEnd={() => handleTouchEnd(girlsCarouselRef)}
          >
            {girls.map((imageUrl, index) => (
              <div
                key={index}
                className={`splash-screen-trending-item ${
                  !isMobile && index === activeGirlIndex
                    ? "splash-screen-trending-item-active"
                    : ""
                }`}
              >
                <div
                  className="splash-screen-trending-placeholder splash-screen-athlete-image"
                  style={{
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="splash-screen-trending-badge">
                    <span className="splash-screen-trending-number">
                      {index + 1}
                    </span>
                    {index < 3 && (
                      <WhatshotIcon className="splash-screen-trending-fire" />
                    )}
                  </div>
                  <div className="splash-screen-trending-player-info">
                    <StarIcon className="splash-screen-trending-star" />
                    <div className="splash-screen-trending-player-stats">
                      <span className="splash-screen-trending-stat">
                        PPG: {girlsStats[index].ppg}
                      </span>
                      <span className="splash-screen-trending-stat">
                        RPG: {girlsStats[index].rpg}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!isMobile && (
            <Tooltip title="Next" placement="right">
              <IconButton
                className="splash-screen-carousel-arrow right"
                onClick={() => scrollCarousel(girlsCarouselRef, "right", false)}
                size="large"
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>

        <div className="splash-screen-carousel-indicators">
          {girls.slice(0, Math.min(5, girls.length)).map((_, index) => (
            <div
              key={index}
              className={`splash-screen-carousel-indicator ${
                index === activeGirlIndex ? "active" : ""
              }`}
              onClick={() => {
                if (girlsCarouselRef.current) {
                  const itemWidth = 180 + 15; // Width + gap
                  girlsCarouselRef.current.scrollTo({
                    left: index * itemWidth,
                    behavior: "smooth",
                  });
                  setActiveGirlIndex(index);
                }
              }}
            />
          ))}
        </div>
      </section>

      {/* Features Section (Splash3) */}
      <section className="splash-screen-features" ref={featuresRef}>
        <div className="splash-screen-features-header">
          <Typography
            variant="h4"
            component="h2"
            className="splash-screen-features-title"
          >
            ELEVATE YOUR GAME
          </Typography>
          <Typography
            variant="subtitle1"
            className="splash-screen-features-subtitle"
          >
            Discover why coaches and scouts choose XSM for unparalleled youth
            sports insights
          </Typography>
          <div className="splash-screen-features-divider">
            <EmojiEventsIcon className="splash-screen-features-trophy" />
          </div>
        </div>

        <Grid
          container
          spacing={3}
          className={`splash-screen-features-container ${
            featuresInView ? "animate-features" : ""
          }`}
        >
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                className={`splash-screen-feature-item ${
                  hoveredFeature === index
                    ? "splash-screen-feature-item-hovered"
                    : ""
                }`}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
                elevation={4}
                style={{
                  borderTop: `4px solid ${feature.color}`,
                  backgroundImage:
                    hoveredFeature === index ? feature.bgImage : "none",
                }}
              >
                <div className="splash-screen-feature-card-content">
                  <div
                    className="splash-screen-feature-icon"
                    style={{
                      backgroundColor: `${feature.color}30`,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </div>

                  <div className="splash-screen-feature-text">
                    <Typography
                      variant="h5"
                      component="h3"
                      className="splash-screen-feature-title"
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      className="splash-screen-feature-description"
                    >
                      {feature.description}
                    </Typography>

                    <div className="splash-screen-feature-stats">
                      {feature.stats.map((stat, i) => (
                        <div key={i} className="splash-screen-feature-stat">
                          <StarIcon
                            className="splash-screen-feature-stat-icon"
                            style={{ color: feature.color }}
                          />
                          <span>{stat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="splash-screen-feature-overlay" />
                </div>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <div className="splash-screen-features-cta">
          <Typography variant="h6" className="splash-screen-features-cta-text">
            Ready to transform your approach to youth sports data?
          </Typography>
          <Typography variant="body1" className="splash-screen-contact-text">
            Contact Us at{" "}
            <span className="splash-screen-email">
              support@xsmbasketball.com
            </span>
          </Typography>
        </div>
      </section>

      {/* FAQ & Footer Section (Splash4) */}
      <section className="splash-screen-faq">
        <h2>Frequently Asked Questions</h2>
        {faqItems.map((item, index) => (
          <div key={index} className="splash-screen-faq-container">
            <div
              className={`splash-screen-faq-item ${
                expandedFaq === index ? "splash-screen-faq-item-active" : ""
              }`}
              onClick={() => toggleFaq(index)}
            >
              <span>{item.question}</span>
              <span className="splash-screen-faq-toggle">
                {expandedFaq === index ? "−" : "+"}
              </span>
            </div>
            <div
              className={`splash-screen-faq-answer ${
                expandedFaq === index ? "splash-screen-faq-answer-visible" : ""
              }`}
            >
              {item.answer}
            </div>
          </div>
        ))}
      </section>
      <footer className="splash-screen-footer">
        <p>Ready to make smarter decisions with data-driven insights?</p>
        <div className="splash-screen-email-form">
          <Link to="/signup" className="text-decoration-none">
            <button className="splash-screen-button splash-screen-button-primary splash-screen-button-large">
              <span className="splash-screen-button-content">
                Get Started{" "}
                <ArrowForwardRoundedIcon className="splash-screen-button-icon" />
              </span>
            </button>
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default SplashScreen;
